import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faImage, 
  faUser, 
  faTimes, 
  faSave
} from '@fortawesome/free-solid-svg-icons';
import AdminLayout from './AdminLayout';
import { ADMIN_URLS } from '../../config/apiConfig';

function EditOfficeBearer() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image_url: '',
    linkedin_url: '',
    term_start: '',
    term_end: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchOfficeBearer();
  }, [id]);
  
  const fetchOfficeBearer = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(ADMIN_URLS.OFFICE_BEARER_DETAIL(id), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/admin/login', { replace: true });
          return;
        }
        throw new Error('Failed to fetch office bearer details');
      }
      
      const data = await response.json();
      
      // Format the date fields (if they exist)
      const formattedData = {
        ...data,
        term_start: data.term_start ? data.term_start.split('T')[0] : '',
        term_end: data.term_end ? data.term_end.split('T')[0] : ''
      };
      
      setFormData(formattedData);
    } catch (error) {
      console.error('Error fetching office bearer:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.position) newErrors.position = 'Position is required';
    if (!formData.bio) newErrors.bio = 'Bio is required';
    if (!formData.image_url) newErrors.image_url = 'Image URL is required';
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(ADMIN_URLS.OFFICE_BEARER_DETAIL(id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/admin/login', { replace: true });
          return;
        }
        
        const data = await response.json();
        throw new Error(data.error || 'Failed to update office bearer');
      }
      
      // Success - redirect to office bearers list
      navigate('/admin/office-bearers');
    } catch (error) {
      console.error('Error updating office bearer:', error);
      alert(`Failed to update office bearer: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Edit Office Bearer</h1>
          <ul className="admin-breadcrumb">
            <li className="admin-breadcrumb-item">
              <Link to="/admin/dashboard">Dashboard</Link>
            </li>
            <li className="admin-breadcrumb-item">
              <Link to="/admin/office-bearers">Office Bearers</Link>
            </li>
            <li className="admin-breadcrumb-item active">Edit Office Bearer</li>
          </ul>
        </div>
        
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '1rem', 
          borderRadius: '0.25rem',
          marginBottom: '1rem' 
        }}>
          {error}
          <div style={{ marginTop: '1rem' }}>
            <button 
              className="admin-btn admin-btn-primary" 
              onClick={() => navigate('/admin/office-bearers')}
            >
              Back to Office Bearers
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Edit Office Bearer</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/office-bearers">Office Bearers</Link>
          </li>
          <li className="admin-breadcrumb-item active">Edit {formData.name}</li>
        </ul>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Office Bearer Information</h2>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="name" className="admin-form-label">Name *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.name ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                {errors.name && <div className="admin-error-message">{errors.name}</div>}
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="position" className="admin-form-label">Position *</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="text"
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.position ? 'admin-input-error' : ''}`}
                    style={{ paddingLeft: '35px' }}
                    placeholder="Enter position (e.g., President, Secretary)"
                  />
                </div>
                {errors.position && <div className="admin-error-message">{errors.position}</div>}
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="term_start" className="admin-form-label">Term Start Date</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="term_start"
                    name="term_start"
                    value={formData.term_start}
                    onChange={handleChange}
                    className="admin-form-control"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
              </div>
              
              <div className="admin-form-group" style={{ flex: 1 }}>
                <label htmlFor="term_end" className="admin-form-label">Term End Date</label>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                  <input
                    type="date"
                    id="term_end"
                    name="term_end"
                    value={formData.term_end}
                    onChange={handleChange}
                    className="admin-form-control"
                    style={{ paddingLeft: '35px' }}
                  />
                </div>
                <small className="admin-form-text">Leave empty if currently serving</small>
              </div>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="image_url" className="admin-form-label">Image URL *</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faImage} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                <input
                  type="text"
                  id="image_url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  className={`admin-form-control ${errors.image_url ? 'admin-input-error' : ''}`}
                  style={{ paddingLeft: '35px' }}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              {errors.image_url && <div className="admin-error-message">{errors.image_url}</div>}
              
              {formData.image_url && (
                <div style={{ marginTop: '1rem' }}>
                  <label className="admin-form-label">Image Preview</label>
                  <div style={{
                    width: '150px',
                    height: '150px',
                    borderRadius: '50%',
                    backgroundImage: `url(${formData.image_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: '#e9ecef',
                    border: '1px solid #ced4da'
                  }}></div>
                </div>
              )}
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="linkedin_url" className="admin-form-label">LinkedIn Profile URL</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: '10px', top: '10px', color: '#6c757d' }} />
                <input
                  type="url"
                  id="linkedin_url"
                  name="linkedin_url"
                  value={formData.linkedin_url || ''}
                  onChange={handleChange}
                  className="admin-form-control"
                  style={{ paddingLeft: '35px' }}
                  placeholder="https://www.linkedin.com/in/profile-name"
                />
              </div>
              <small className="admin-form-text">Optional: Add LinkedIn profile URL for professional networking</small>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="bio" className="admin-form-label">Bio *</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                className={`admin-form-control ${errors.bio ? 'admin-input-error' : ''}`}
                rows="6"
                placeholder="Enter a brief professional bio"
              ></textarea>
              {errors.bio && <div className="admin-error-message">{errors.bio}</div>}
            </div>
            
            <div className="admin-form-actions" style={{ marginTop: '2rem' }}>
              <Link
                to="/admin/office-bearers"
                className="admin-btn admin-btn-light admin-btn-with-icon"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Cancel</span>
              </Link>
              <button
                type="submit"
                className="admin-btn admin-btn-primary admin-btn-with-icon"
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{isSubmitting ? 'Saving...' : 'Update Office Bearer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default EditOfficeBearer;
